/* eslint-disable import/prefer-default-export */
import Mustache from 'mustache';
import DOMPurify from 'isomorphic-dompurify';
import striptags from 'striptags';
import { isDOMPurifySupported } from '../Components/Html/HtmlHelpers';
import { getDisplayValueFromComponent } from './components';
import { exposeEventToPartner } from './events';

// Keep track of invalid replacements already notified to prevent duplicate notifications on re-renders
const invalidReplacementsAlreadyNotified = [];

/**
 * Replaces [[supplierMatchCount]] placeholder with actual matched brands count
 * @param {string} template - The template string to process
 * @param {object} supplierMatches - The supplierMatches state from Redux
 * @param {boolean} showBrandMatchCount - Feature flag to enable replacement
 * @returns {string} - Template with replacements applied
 */
export const replaceSupplierMatchCountPlaceholder = (
  template,
  supplierMatches,
  showBrandMatchCount
) => {
  if (typeof template !== 'string') {
    return template;
  }

  if (!showBrandMatchCount || !template.includes('[[supplierMatchCount]]')) {
    return template;
  }

  const { matchingSupplierCount } = supplierMatches || {};

  if (typeof matchingSupplierCount === 'number') {
    return template.replace(
      /\[\[supplierMatchCount\]\]/g,
      String(matchingSupplierCount)
    );
  }

  return template;
};

export const renderMustache = ({
  template,
  components,
  currentComponent,
  iFrameId,
  formId,
  supplierMatches,
  showBrandMatchCount,
}) => {
  if (typeof template !== 'string') {
    return '';
  }

  const processedTemplate = replaceSupplierMatchCountPlaceholder(
    template,
    supplierMatches,
    showBrandMatchCount
  );

  if (!processedTemplate.includes('{{')) {
    // If there are no mustache replacements, return the template as is
    // This prevents unnecessary processing since this is called on every render
    return processedTemplate;
  }

  const invalidReplacements = getInvalidTemplateReplacements(
    processedTemplate,
    components
  );
  invalidReplacements.forEach((invalidReplacement) => {
    const eventObject = {
      type: 'mustache',
      pageNumber:
        currentComponent?.component?.page != null
          ? currentComponent.component.page + 1
          : null,
      componentInputType: currentComponent?.input?.type,
      componentId: currentComponent?.component.id,
      iFrameId,
      formId,
    };
    if (typeof window !== 'undefined') {
      if (!invalidReplacementsAlreadyNotified.includes(invalidReplacement)) {
        exposeEventToPartner({
          eventType: 'formError',
          eventObject,
          errorObject: {
            message: `There was an error when parsing the mustache template. Invalid replacement: ${invalidReplacement}`,
          },
        });
        invalidReplacementsAlreadyNotified.push(invalidReplacement);
      }
    }
  });

  const data = {};

  Object.values(components).forEach((component) => {
    if (!component?.input?.value) {
      return '';
    }

    const rawValue = getDisplayValueFromComponent(component);

    let value;

    if (!isDOMPurifySupported()) {
      value = striptags(String(rawValue));
    } else {
      value = DOMPurify.sanitize(String(rawValue), {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });
    }

    if (component.component.mappedQuestionId) {
      data[`answer:${component.component.mappedQuestionId}`] = value;
    }
  });

  const content = Mustache.render(processedTemplate, data);
  return decodeSpecialCharacters(content);
};

export const getInvalidTemplateReplacements = (template, components) => {
  const regex = /{{\s?answer:(\w+)\s?}}/g;
  const matches = [...template.matchAll(regex)];
  const answerIds = matches.map((match) => match[1]);

  return answerIds.filter((answerId) => {
    const foundComponent = Object.values(components).find(
      (component) =>
        component.component.mappedQuestionId === answerId ||
        String(component.component.id) === answerId
    );

    if (!foundComponent) {
      return true;
    }

    if (!foundComponent?.input?.value) {
      return true;
    }

    const value = getDisplayValueFromComponent(foundComponent);
    return !value; // Invalid if no display value
  });
};

const decodeSpecialCharacters = (content) => {
  // Mustache will do it's own escaping, so we need to unescape special characters we want to render
  let decodedContent = content;
  decodedContent = decodedContent.replace(/&amp;/g, '&'); // Replace encoded ampersands with ampersands
  decodedContent = decodedContent.replace(/&#x2F;/g, '/'); // Replace encoded forward slashes with forward slashes
  return decodedContent;
};
